/**
 * widgets/TopNav.ts · 顶部 NAV (唯一来源)
 *
 * 替代 v200.x 多 IIFE 各自加 NAV 元素的混乱:
 *   - 1 个 widget · 1 处定义
 *   - 不让多 IIFE 抢 #tn-view-as-role-btn / #tn-syncCenter-btn 等
 *
 * 内容:
 *   - 左: Logo "中南 MES" + 版本
 *   - 中: 搜索框 (Cmd+K 触发全局搜索)
 *   - 右: 用户名 · 角色 · 通知 badge · 语言切换 · 登出
 */

import type { User } from '@core/auth';
import { logout } from '@core/auth';
import { setLang, getCurrentLang, t } from '@core/i18n';
import { navigate } from '@core/router';
import { APP_VERSION } from '@/constants/company';
import { renderNotificationBell } from './NotificationBell';

export function renderTopNav(user: User): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = 'top-nav-bar bg-gray-900 text-white sticky top-0 z-40';
  nav.style.willChange = 'transform'; // GPU 层 · 防 NAV 抖
  nav.innerHTML = `
    <div class="flex items-center justify-between px-4 py-2 max-w-full">
      <!-- 左: Logo -->
      <div class="flex items-center gap-3">
        <button class="text-xl font-bold" onclick="window.location.hash='#${getRoleHome(user.role)}'" translate="no">
          中南 MES
        </button>
        <span class="text-xs opacity-50">v${APP_VERSION}</span>
      </div>

      <!-- 中: 搜索框 (Cmd+K) -->
      <div class="flex-1 max-w-xl mx-4 hidden md:block">
        <button id="tn-cmdk-btn" class="w-full text-left px-3 py-1.5 rounded bg-gray-800 text-gray-400 text-sm hover:bg-gray-700">
          🔍 ${t('nav.search') || '搜索工单/客户/产品'} (⌘K)
        </button>
      </div>

      <!-- 右: 用户 + 操作 -->
      <div class="flex items-center gap-3 text-sm">
        <button class="px-2 py-1 hover:bg-gray-800 rounded" id="tn-lang-toggle">
          ${getCurrentLang().toUpperCase()}
        </button>
        <span id="tn-notif-bell-slot"></span>
        <div class="border-l border-gray-700 pl-3 flex items-center gap-2">
          <span class="opacity-80">${escapeHtml(user.code)}</span>
          <span class="hidden md:inline opacity-60">·</span>
          <span class="hidden md:inline opacity-80">${escapeHtml(user.name)}</span>
        </div>
        <button class="px-2 py-1 hover:bg-red-900 rounded text-red-300" id="tn-logout-btn" title="${t('nav.logout') || '登出'}">
          ⏻
        </button>
      </div>
    </div>
  `;

  // 事件绑定 (单一来源 · 不让多 IIFE 重绑)
  const langBtn = nav.querySelector<HTMLButtonElement>('#tn-lang-toggle');
  langBtn?.addEventListener('click', () => {
    const cur = getCurrentLang();
    const next = cur === 'zh' ? 'vi' : cur === 'vi' ? 'en' : 'zh';
    setLang(next).then(() => {
      // 重渲染当前页 (i18n 已变)
      const hash = window.location.hash;
      window.location.hash = '';
      window.location.hash = hash;
    });
  });

  const logoutBtn = nav.querySelector<HTMLButtonElement>('#tn-logout-btn');
  logoutBtn?.addEventListener('click', () => {
    if (confirm(t('nav.logout_confirm') || '确定登出?')) logout();
  });

  const cmdkBtn = nav.querySelector<HTMLButtonElement>('#tn-cmdk-btn');
  cmdkBtn?.addEventListener('click', () => navigate('search'));

  // 替换 bell slot 为带 badge 的真 widget (polling 30s)
  const bellSlot = nav.querySelector('#tn-notif-bell-slot');
  if (bellSlot) bellSlot.replaceWith(renderNotificationBell(user));

  return nav;
}

function getRoleHome(role: string): string {
  const map: Record<string, string> = {
    admin: 'gmHome', gm: 'gmHome', boss: 'gmHome',
    sales: 'salesHome', engineer: 'engineerHome', supervisor: 'supervisorHome',
    worker: 'workerHome', quality: 'qualityHome', warehouse: 'warehouseHome',
    maintenance: 'maintHome', finance: 'financeHome', hr: 'hrHome',
    purchasing: 'purchasingHome', guard: 'guardHome', iso: 'isoHome',
  };
  return map[role] || 'gmHome';
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
